import React from "react";
import { ActivityIndicator, ScrollView, StyleSheet, View } from "react-native";
import { useFocusEffect, useIsFocused, useRoute } from "@react-navigation/native";
import { tRep, tRepCategory, tUser, tVendorData } from "tmt_api/src/types/types";
import { queryUser } from "src/db/user.db";
import { documentRep } from "src/db/rep.db";
import { kColors } from "src/kColors";
import firebase from "firebase";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { PdFormLabel } from "src/components/form-control";
import { PdButton, PdButtonLabel } from "src/components/forms/button";
import { tPdInputPdfValue } from "src/components/forms/input";
import { tImagePickerValue } from "src/components/forms/picker";
import {
  PdHookFormCheckboxGroup,
  PdHookFormErrorMessage,
  PdHookFormImagePicker,
  PdHookFormInput,
  PdHookFormInputPdf,
  PdHookFormInputPhone,
  PdHookFormSelect,
} from "src/components/hook-form";
import { PdStack, PdText } from "src/components/layout";
import { PdDivider } from "src/components/layout/table";
import { useChangeEmailModal, useVendorSetupAccount } from "src/hooks/modals";
import { useIsMobile } from "src/hooks/ui";
import { kStyleConst } from "src/kStyle";
import { useAppDispatch } from "src/redux";
import { updateRepProfile } from "src/redux/company/actions";
import { repCategories, usStateOptions } from "src/util/comboBoxData";
import { getSafeUrl } from "src/util/functions";
import { PdFormCard, PdSectionBadge, PdSectionBadgeLabel } from "./P09_Profile/components";
import { PdCenter } from "src/components/v2/ui/core";

type tAdminRepSettingsParams = {
  params: { repId?: string };
  key: string;
  name: string;
};

type tRepFormData = {
  name: string;
  website: string;
  linkedin: string;
  instagram: string;
  facebook: string;
  twitter: string;
  aboutMe: string;
  businessName: string;
  phone: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: { value: string; label: string } | null;
  zipCode: string;
  categories: tRepCategory[];
  taxInfo: tPdInputPdfValue;
  taxId: string;
  avatar: tImagePickerValue | null;
};

const AdminRepForm: React.FC<{ rep: tRep; user: tUser }> = ({ rep, user }) => {
  const isMobile = useIsMobile();
  const dispatch = useAppDispatch();
  const rUser = useSelector((state) => state.UserReducer.profile);
  const vendorSetupAccount = useVendorSetupAccount();
  const changeEmailModal = useChangeEmailModal();
  const formMethods = useForm<tRepFormData>({
    defaultValues: {
      name: rep.name || "",
      website: rep.website || "",
      linkedin: rep.socialMedia?.linkedin ?? "",
      instagram: rep.socialMedia?.instagram ?? "",
      facebook: rep.socialMedia?.facebook ?? "",
      twitter: rep.socialMedia?.twitter ?? "",
      aboutMe: rep.aboutMe || "",
      businessName: rep.company || "",
      phone: rep.phone || "",
      email: rep.email || "",
      addressLine1: rep.address.address1,
      addressLine2: rep.address.address2,
      city: rep.address.city,
      state: rep.address.state_or_province
        ? usStateOptions.find((state) => [state.label, state.value].includes(rep.address.state_or_province)) ?? null
        : null,
      zipCode: rep.address.postal_code || "",
      categories: rep.category || [],
      taxInfo: rep.taxInfo ? { uri: rep.taxInfo.url, name: rep.taxInfo.name } : null,
      taxId: rep.taxId || "",
      avatar: rep.avatar ? { uri: rep.avatar } : null,
    },
  });
  const categories = useWatch({ name: "categories", control: formMethods.control });
  const [isBankConnected, setBankConnected] = React.useState(!!rep.vendorId);
  const [isSubmitting, setSubmitting] = React.useState(false);

  const handleOnPressSetupAccount = async () => {
    const defaultValue: tVendorData = {
      businessName: rep.name ?? "",
      businessDomain: "",
      ein: rep.taxId ?? "",
      url: "",
      emailAddress: rep.email ?? "",
      address1: rep.address.address1,
      address2: rep.address.address2 ?? "",
      city: rep.address.city,
      state: rep.address.state_or_province,
      zipCode: rep.address.postal_code,
      phone: rep.phone ?? "",
      country: rep.address.country_code,
      accountNumber: "",
      routingNumber: "",
    };
    const response = await vendorSetupAccount.openAsync({ mode: "rep", defaultValue, isEditing: isBankConnected });
    if (response.status === "closed") return;
    setBankConnected(true);
  };

  const handleOnPressChangeEmail = () => {
    changeEmailModal.openAsync({ accountType: "rep", user });
  };

  const handleOnSubmit = formMethods.handleSubmit(async (formData) => {
    try {
      setSubmitting(true);
      const payload: tRep = {
        ...rep,
        name: formData.name,
        website: formData.website ?? "",
        socialMedia: {
          linkedin: getSafeUrl(formData.linkedin),
          instagram: getSafeUrl(formData.instagram),
          facebook: getSafeUrl(formData.facebook),
          twitter: getSafeUrl(formData.twitter),
        },
        aboutMe: formData.aboutMe,
        company: formData.businessName,
        phone: formData.phone,
        email: formData.email,
        address: {
          ...rep.address,
          address1: formData.addressLine1 ?? "",
          address2: formData.addressLine2,
          city: formData.city,
          state_or_province: formData.state?.value ?? "",
          postal_code: formData.zipCode ?? "",
        },
        avatar: formData.avatar?.uri ?? "",
        category: formData.categories,
      };

      if (formData.taxInfo?.file) {
        const snapshot = await firebase.storage().ref(`rep/${rep.id}/uploads/tax_info.pdf`).put(formData.taxInfo.file);
        const url: string = await snapshot.ref.getDownloadURL();
        rep.taxInfo = { name: formData.taxInfo?.name ?? "", url };
      }
      if (formData.avatar?.file) {
        const fileExtension = formData.avatar.file.type.replace(/^.*\//gm, "");
        const snapshot = await firebase
          .storage()
          .ref(`rep/${rep.id}/uploads/profile.${fileExtension}`)
          .put(formData.avatar.file);
        rep.avatar = await snapshot.ref.getDownloadURL();
      }
      await dispatch(updateRepProfile(payload, rUser));
      setSubmitting(false);
    } catch (error) {
      setSubmitting(false);
    }
  });

  return (
    <FormProvider {...formMethods}>
      <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1]} gap={3} style={styles.formSection}>
        <PdFormCard style={styles.zIndex1}>
          <View style={styles.formSectionTitle}>
            <PdSectionBadge>
              <PdSectionBadgeLabel>1</PdSectionBadgeLabel>
            </PdSectionBadge>
            <PdText variant="fieldset" style={styles.ml2}>
              The Basics
            </PdText>
          </View>
          <PdDivider style={styles.formSectionDivider} />
          <View style={styles.mb3}>
            <PdFormLabel>Primary Contact Full Name *</PdFormLabel>
            <PdHookFormInput name="name" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="name" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Business / Company Name *</PdFormLabel>
            <PdHookFormInput name="businessName" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="businessName" />
          </View>
          <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1]} gap={3} style={styles.formSection}>
            <View>
              <PdFormLabel>Primary Phone Number *</PdFormLabel>
              <PdHookFormInputPhone
                name="phone"
                placeholder="(XXX) XXX-XXXX"
                autoCompleteType="tel"
                rules={{ required: "Required" }}
                onSubmitEditing={handleOnSubmit}
              />
              <PdHookFormErrorMessage name="phone" />
            </View>
            <View>
              <PdFormLabel>Primary Email *</PdFormLabel>
              <PdHookFormInput
                isDisabled
                name="email"
                rules={{ required: "Required" }}
                onSubmitEditing={handleOnSubmit}
              />
              <PdHookFormErrorMessage name="email" />
            </View>
          </PdStack>
          <View style={styles.mb3}>
            <PdFormLabel>Primary Adress Line 1 *</PdFormLabel>
            <PdHookFormInput name="addressLine1" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
            <PdHookFormErrorMessage name="addressLine1" />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Primary Adress Line 2</PdFormLabel>
            <PdHookFormInput name="addressLine2" onSubmitEditing={handleOnSubmit} />
          </View>
          <PdStack
            direction={isMobile ? "vertical" : "horizontal"}
            layout={[2, 1, 1]}
            gap={3}
            style={styles.selectFormSection}
          >
            <View>
              <PdFormLabel>Primary City *</PdFormLabel>
              <PdHookFormInput name="city" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
              <PdHookFormErrorMessage name="city" />
            </View>
            <View style={styles.zIndex1}>
              <PdFormLabel>State *</PdFormLabel>
              <PdHookFormSelect
                name="state"
                rules={{ required: "Required" }}
                options={usStateOptions}
                isClearable={false}
                onSubmitEditing={handleOnSubmit}
              />
              <PdHookFormErrorMessage name="state" />
            </View>
            <View>
              <PdFormLabel>Zip *</PdFormLabel>
              <PdHookFormInput name="zipCode" rules={{ required: "Required" }} onSubmitEditing={handleOnSubmit} />
              <PdHookFormErrorMessage name="zipCode" />
            </View>
          </PdStack>
          <View>
            <PdFormLabel>What category best describes your Brands (select up to 3) *</PdFormLabel>
            <PdHookFormCheckboxGroup
              name="categories"
              isDisabled={categories.length > 2}
              rules={{
                required: "Required",
                validate: (value: string[]) => (value.length > 3 ? "You can select up to 3 categories" : true),
              }}
              style={styles.mt2}
              options={repCategories}
            />
            <PdHookFormErrorMessage name="categories" />
          </View>
        </PdFormCard>
        <PdFormCard>
          <View style={styles.formSectionTitle}>
            <PdSectionBadge>
              <PdSectionBadgeLabel>2</PdSectionBadgeLabel>
            </PdSectionBadge>
            <PdText variant="fieldset" style={styles.ml2}>
              My Profile
            </PdText>
          </View>
          <PdDivider style={styles.formSectionDivider} />
          <View style={styles.mb3}>
            <PdFormLabel>Profile Image</PdFormLabel>
            <PdStack style={styles.profileImageFormLine} direction={isMobile ? "vertical" : "horizontal"}>
              <PdHookFormImagePicker
                name="avatar"
                style={styles.avatarImagePicker}
                mobileStyle={styles.mobileAvatarImagePicker}
                webStyle={webStyles.avatarImagePicker}
              />
              <PdText style={styles.profileImageText}>Upload an image of your self of company logo.</PdText>
            </PdStack>
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>About Me</PdFormLabel>
            <PdHookFormInput
              name="aboutMe"
              multiline
              numberOfLines={12}
              placeholder="Write a brief bio about you and your business. This will be visible to brands and retailers when they view your profile."
            />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Website</PdFormLabel>
            <PdHookFormInput name="website" onSubmitEditing={handleOnSubmit} />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Linkedin</PdFormLabel>
            <PdHookFormInput name="linkedin" onSubmitEditing={handleOnSubmit} />
          </View>
          <View style={styles.mb3}>
            <PdFormLabel>Instagram</PdFormLabel>
            <PdHookFormInput name="instagram" onSubmitEditing={handleOnSubmit} />
          </View>
          <View>
            <PdFormLabel>Facebook</PdFormLabel>
            <PdHookFormInput name="facebook" onSubmitEditing={handleOnSubmit} />
          </View>
        </PdFormCard>
      </PdStack>

      <PdFormCard style={styles.formSection}>
        <View style={styles.formSectionTitle}>
          <PdSectionBadge>
            <PdSectionBadgeLabel>3</PdSectionBadgeLabel>
          </PdSectionBadge>
          <PdText variant="fieldset" style={styles.ml2}>
            Admin
          </PdText>
        </View>
        <PdDivider style={styles.formSectionDivider} />

        <PdStack direction={isMobile ? "vertical" : "horizontal"} layout={[1, 1]} gap={3}>
          <View>
            <View style={styles.mb3}>
              <PdFormLabel>Resale / Business Certificate</PdFormLabel>
              <PdHookFormInputPdf name="taxInfo" isMobile={isMobile} />
              <PdHookFormErrorMessage name="taxInfo" />
            </View>
            <View style={styles.mb3}>
              <PdFormLabel>Tax ID Number</PdFormLabel>
              <PdHookFormInput name="taxId" />
              <PdHookFormErrorMessage name="taxId" />
            </View>

            <PdButton
              variant="outline"
              onPress={handleOnPressSetupAccount}
              style={isMobile ? styles.mobileBankAccountButton : styles.webBankAccontButton}
            >
              <PdButtonLabel>{isBankConnected ? "Update Bank Account" : "Connect Bank Account"}</PdButtonLabel>
            </PdButton>
            <PdText style={styles.bankAccountText}>
              Commissions you earn will be deposited directly into this bank account.
            </PdText>
          </View>
          <View />
        </PdStack>
      </PdFormCard>

      <PdFormCard style={styles.formSection}>
        <View style={styles.formSectionTitle}>
          <PdSectionBadge>
            <PdSectionBadgeLabel>4</PdSectionBadgeLabel>
          </PdSectionBadge>
          <PdText variant="fieldset" style={styles.ml2}>
            TMT Admin Abilites
          </PdText>
        </View>
        <PdDivider style={styles.formSectionDivider} />
        <View style={isMobile ? styles.tmtAdminAbilitiesContentMobile : styles.tmtAdminAbilitiesContent}>
          <View style={styles.mb3}>
            <PdFormLabel>Primary Email</PdFormLabel>
            <PdText style={styles.mb3}>{user.email}</PdText>
            <PdButton
              onPress={handleOnPressChangeEmail}
              isDisabled={user.isDeactivated}
              size="sm"
              colorScheme="pink"
              style={styles.mrAuto}
            >
              <PdButtonLabel>Change Email</PdButtonLabel>
            </PdButton>
          </View>
        </View>
      </PdFormCard>

      <PdButton isLoading={isSubmitting} onPress={handleOnSubmit} style={isMobile ? styles.flex1 : styles.mlAuto}>
        <PdButtonLabel>Save Changes</PdButtonLabel>
      </PdButton>
    </FormProvider>
  );
};

const P06_10_AdminRepSettings: React.FC = () => {
  const [repUser, setRepUser] = React.useState<tUser | undefined>(undefined);
  const [rep, setRep] = React.useState<tRep | undefined>(undefined);
  const isFocused = useIsFocused();
  const route = useRoute<tAdminRepSettingsParams>();

  useFocusEffect(
    React.useCallback(() => {
      let userUnsubscribe: (() => void) | undefined;
      let repUnsubscribe: (() => void) | undefined;

      if (route?.params?.repId) {
        userUnsubscribe = queryUser({ companyRefPath: `reps/${route?.params?.repId}` }).onSnapshot((snapshot) => {
          setRepUser(snapshot.docs[0]?.data());
        });
        repUnsubscribe = documentRep(route?.params?.repId).onSnapshot((document) => {
          setRep(document.data());
        });
      }

      return () => {
        userUnsubscribe?.();
        repUnsubscribe?.();
      };
    }, [route?.params?.repId]),
  );

  React.useEffect(() => {
    if (!isFocused) {
      setRepUser(undefined);
      setRep(undefined);
    }
  }, [isFocused]);

  if (!repUser || !rep) {
    return (
      <PdCenter>
        <ActivityIndicator size="large" color={kColors.brandDarkGreen} />
      </PdCenter>
    );
  }

  return (
    <ScrollView style={[styles.p6, styles.bgLightGray]}>
      <AdminRepForm rep={rep} user={repUser} />
    </ScrollView>
  );
};

export const P06_11 = React.memo(P06_10_AdminRepSettings);

const styles = StyleSheet.create({
  avatarImagePicker: {
    alignItems: "center",
    backgroundColor: kColors.backgroundGrey,
    borderRadius: 999,
    height: 141,
    justifyContent: "center",
    marginTop: 1 * kStyleConst.kSpacing,
    width: 141,
  },
  bankAccountText: {
    color: kColors.gray500,
    fontSize: 12,
  },
  bgLightGray: {
    backgroundColor: kColors.lightGray,
  },
  flex1: {
    flex: 1,
  },
  formSection: {
    marginBottom: kStyleConst.kSpacing * 3,
    width: "100%",
  },
  formSectionDivider: {
    marginBottom: 4 * kStyleConst.kSpacing,
    marginTop: 1 * kStyleConst.kSpacing,
  },
  formSectionTitle: {
    alignItems: "center",
    flexDirection: "row",
  },
  mb3: {
    marginBottom: kStyleConst.kSpacing * 3,
  },
  ml2: {
    marginLeft: kStyleConst.kSpacing * 2,
  },
  mlAuto: {
    marginLeft: "auto",
  },
  mobileAvatarImagePicker: {
    borderRadius: 999,
    height: 141,
    width: 141,
  },
  mobileBankAccountButton: {
    marginBottom: kStyleConst.kSpacing * 1,
  },
  mrAuto: {
    marginRight: "auto",
  },
  mt2: {
    marginTop: kStyleConst.kSpacing * 2,
  },
  p6: {
    padding: kStyleConst.kSpacing * 6,
  },
  profileImageFormLine: {
    alignItems: "center",
  },
  profileImageText: {
    color: kColors.gray500,
    fontSize: 12,
  },
  selectFormSection: {
    marginBottom: kStyleConst.kSpacing * 3,
    width: "100%",
    zIndex: 1,
  },
  tmtAdminAbilitiesContent: {
    width: "50%",
  },
  tmtAdminAbilitiesContentMobile: {
    width: "100%",
  },
  webBankAccontButton: {
    marginBottom: kStyleConst.kSpacing * 1,
    marginRight: "auto",
  },
  zIndex1: {
    zIndex: 1,
  },
});

const webStyles: { [key: string]: React.CSSProperties } = {
  avatarImagePicker: {
    width: 141,
    height: 141,
    borderRadius: 999,
  },
} as const;
